/* HowItWorks — flujo del usuario final en 3 pasos.
   Fondo claro (sand-50) para descansar visualmente después de WhatIs (sand-100). */

.section {
  background: var(--sand-100);
  padding-block: var(--space-20);
}

@media (min-width: 768px) {
  .section {
    padding-block: var(--space-24);
  }
}

@media (min-width: 1024px) {
  .section {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

/* ─── Grilla de pasos ─── */

.steps {
  list-style: none;
  padding: 0;
  margin: var(--space-12) 0 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-10);
  counter-reset: step;
}

@media (min-width: 768px) {
  .steps {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-8);
    margin-top: var(--space-16);
  }
}

@media (min-width: 1024px) {
  .steps {
    gap: var(--space-12);
  }
}

/* ─── Step ─── */

.step {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);

  /* Reveal — fade-in + slide-up, stagger 150ms (--i = 0/1/2) */
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 600ms var(--ease-decelerate),
    transform 600ms var(--ease-decelerate);
  transition-delay: calc(var(--i, 0) * 150ms);
  will-change: transform, opacity;
}

.revealed .step {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .step {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ─── Número grande ─── */

.number {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: clamp(56px, 8vw, 88px);
  line-height: 0.9;
  letter-spacing: -0.04em;
  color: transparent;
  -webkit-text-stroke: 1.5px var(--sand-300);
  margin-bottom: var(--space-1);
  user-select: none;
}

/* ─── Mockup ─── */

.mockup {
  width: 100%;
  max-width: 320px;
  align-self: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-xl);
  padding: var(--space-4);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  transition:
    transform var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
}

@media (min-width: 768px) {
  .mockup {
    align-self: stretch;
    max-width: none;
  }
}

.revealed .step:hover .mockup,
.revealed .step:focus-within .mockup {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

/* ─── Texto del paso ─── */

.stepTitle {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-xl);
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: var(--space-2) 0 0;
}

.stepBody {
  font-family: var(--font-body);
  font-size: var(--text-base);
  line-height: 1.6;
  color: var(--color-text-muted);
  margin: 0;
}

/* ─── Línea de cierre ─── */

.closing {
  position: relative;
  margin: var(--space-16) auto 0;
  max-width: 720px;
  text-align: center;
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: clamp(20px, 2.6vw, 26px);
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: var(--color-text);
  padding: var(--space-6) var(--space-6);
  background: var(--jade-50);
  border-radius: var(--radius-xl);
  border: 1px solid color-mix(in srgb, var(--jade-500) 18%, transparent);
}

.closingMark {
  position: absolute;
  top: 0;
  left: 50%;
  width: 48px;
  height: 4px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  transform: translate(-50%, -50%);
}

@media (min-width: 768px) {
  .closing {
    margin-top: var(--space-20);
    padding: var(--space-8);
  }
}

/* ════════════════════════════════════════════════════════════════════════════
   Mockups — pantallas estilizadas (sin chrome de teléfono)
   ════════════════════════════════════════════════════════════════════════════ */

.mockSurface {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.mockSearch {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--sand-100);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  font-family: var(--font-body);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.mockFilters {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.mockFilterChip {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--blue-700);
  background: var(--blue-50);
  border: 1px solid color-mix(in srgb, var(--blue-500) 22%, transparent);
  padding: 3px var(--space-2);
  border-radius: var(--radius-full);
}

.mockList {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.mockRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  background: var(--sand-50);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
}

.mockAvatar,
.mockAvatarLg {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--color-text-inverse);
  border-radius: var(--radius-full);
}

.mockAvatar {
  width: 28px;
  height: 28px;
  font-size: 11px;
}

.mockAvatarLg {
  width: 56px;
  height: 56px;
  font-size: var(--text-lg);
}

/* Variaciones de color por índice — paleta visualmente alineada con el brand */
.mockAvatar0 { background: var(--blue-500); }
.mockAvatar1 { background: var(--jade-500); }
.mockAvatar2 { background: var(--blue-700); }

.mockRowText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.mockRowName {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: var(--text-xs);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mockRowMeta {
  font-family: var(--font-body);
  font-size: 10px;
  color: var(--color-text-muted);
}

/* ─── Perfil ─── */

.mockProfileTop {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-1);
  padding: var(--space-2) 0;
}

.mockChips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  justify-content: center;
}

.mockChip {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 500;
  color: var(--color-text-muted);
  background: var(--sand-100);
  padding: 3px var(--space-2);
  border-radius: var(--radius-full);
}

.mockBio {
  font-family: var(--font-body);
  font-size: 11px;
  line-height: 1.5;
  color: var(--color-text-muted);
  margin: 0;
  text-align: center;
}

/* ─── Canales de contacto ─── */

.mockChannels {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.mockChannel {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  background: var(--sand-50);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
}

.mockChannelIcon {
  flex: none;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse);
  border-radius: var(--radius-full);
}

/* Brand colors — third-party marks */
.mockChannelIcon_whatsapp { background: #25D366; }
.mockChannelIcon_instagram {
  background: linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%);
}
.mockChannelIcon_linkedin { background: #0A66C2; }
.mockChannelIcon_x { background: #000000; }
.mockChannelIcon_web {
  background: var(--sand-200);
  color: var(--sand-900);
}
